import React from 'react'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import { useDispatch } from 'react-redux';
import { wrapper } from '../store/configStore';
import { SERVER_IP } from '../store/middleware/api';
import axios from 'axios';
import ClientReviews from '../components/ClientReviews';
import LandingCategories from '../components/LandingCategories';
import PastEvents from '../components/PastEvents';
import visualsHeroV2 from '../public/Pictures/categories-v2/visuals-hero-v2.jpg'
import PageShell from '../components/design-system/foundations/PageShell';
import ServiceHero from '../components/design-system/marketing/ServiceHero';
import VisualDecisionGuide from '../components/design-system/marketing/VisualDecisionGuide';
import QuotePop from '../components/DeferredQuotePopup';
import { SHOW_POPUP } from '../store/slices/ui';
import { FiImage } from '@react-icons/all-files/fi/FiImage';
import { FiLayers } from '@react-icons/all-files/fi/FiLayers';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor';
import * as gtag from '../lib/gtag';
import { compactPublicEvents } from '../utilities/eventPublicPayload';
import {
    getVisualFamilyBreadcrumbs,
    toBreadcrumbJsonLdItems,
    toHeroBreadcrumbs
} from '../utilities/visualFamilyBreadcrumbs';



export const getStaticProps = wrapper.getStaticProps(
    async () => {
        const corp = await axios.request(`${SERVER_IP}/user/static-landing`)

        let { newsletterevents, total_events } = corp?.data
        return {
            props: {
                newsletterevents: compactPublicEvents(newsletterevents), total_events
            }
        }
    }
);

const VisualsRental = ({ newsletterevents, total_events }) => {
    const dispatch = useDispatch()
    const openQuote = () => {
        gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
        dispatch(SHOW_POPUP({
            key: 'quote',
            size: 'medium',
            body: <QuotePop />
        }))
    }
    const heroSteps = [
        { icon: 'search', title: 'Choose your display', description: 'Compare LED walls, projectors, screens, and TVs.' },
        { icon: 'calendar', title: 'Share dates & venue', description: 'Tell us where and when your event takes place.' },
        { icon: 'truck', title: 'Delivery & installation', description: 'Our crew installs, connects, and tests the visual system.' },
        { icon: 'success', title: 'Show-ready visuals', description: 'Present with dependable local technical support.' }
    ]
    const breadcrumbs = getVisualFamilyBreadcrumbs('/visuals')
    const displayOptions = [
        {
            label: 'Smaller rooms and flexible layouts',
            title: 'TV displays',
            icon: FiMonitor,
            summary: 'A practical event display rental for breakout rooms, registration, digital signage, and presentations that need one or several screens.',
            points: ['Best for closer viewing', 'Simple stand-mounted layouts', 'Supports multi-screen event plans'],
            href: '/tv-rental',
            actionLabel: 'View TV rentals'
        },
        {
            label: 'Presentations and screenings',
            title: 'Projectors and screens',
            icon: FiImage,
            summary: 'A scalable presentation equipment rental for conferences, meetings, movie nights, and indoor venues where throw distance and room light can be controlled.',
            points: ['Flexible screen sizes', 'Brightness matched to the room', 'Short- and standard-throw options'],
            href: '/projector-rental',
            actionLabel: 'View projector rentals'
        },
        {
            label: 'Large-format and high-brightness',
            title: 'LED video walls',
            icon: FiLayers,
            summary: 'A modular visual display rental for trade shows, conferences, stages, and outdoor or brightly lit environments that need strong visual impact.',
            points: ['Custom dimensions and pixel pitch', 'Indoor and outdoor configurations', 'Professional installation included'],
            href: '/led-video-wall-rental',
            actionLabel: 'View LED video walls'
        }
    ]

    return <>
        <BreadcrumbJsonLd itemListElements={toBreadcrumbJsonLdItems(breadcrumbs)} />
        <NextSeo
            title='Visual Equipment Rental for Events in South Florida'
            description='Compare TV displays, projectors and screens, and LED video walls for conferences, trade shows, and events in Miami, Fort Lauderdale, and South Florida.'
            canonical="https://miamisoundrental.com/visuals"
            twitter={{
                handle: '@miamisoundrent1',
                site: '@miamisoundrent1',
                cardType: 'summary_large_image',
            }}
            openGraph={{
                type: 'website',
                url: `https://miamisoundrental.com/visuals`,
                title: 'Visual Equipment Rental for Events in South Florida',
                description: 'Compare TV displays, projectors and screens, and LED video walls for conferences, trade shows, and events in Miami, Fort Lauderdale, and South Florida.',
                images: [
                    {
                        url: 'https://miamisoundrental.com/Pictures/categories-v2/visuals-hero-v2.jpg',
                        width: 1915,
                        height: 821,
                        alt: 'Corporate ballroom with a professionally installed LED video wall in Miami',
                    },

                ],
                site_name: 'Miami Sound Rental',
            }}
        />
        <div className='categoryRental projectorRental'>
            <PageShell>
                <ServiceHero
                    breadcrumbs={toHeroBreadcrumbs(breadcrumbs)}
                    eyebrow='Visual equipment rental · Miami & Fort Lauderdale'
                    title='Visual Equipment Rental for Events'
                    description='Compare TVs, projectors and screens, and LED video walls by audience size, viewing distance, venue lighting, installation needs, and budget.'
                    image={{ src: visualsHeroV2, position: 'center' }}
                    imageAlt='Corporate ballroom with a professionally installed LED video wall in Miami'
                    primaryAction={{
                        label: 'Choose Your Display Type',
                        href: '#visualCategories',
                        focusTargetOnNavigate: true,
                        icon: FiMonitor,
                        conversionKey: 'visuals.hero.browse-categories',
                        channel: 'rental',
                        salesPath: 'self_service',
                        onClick: () => gtag.event({
                            action: 'catalog_click',
                            label: 'leads',
                            value: 2,
                            category: 'orders'
                        })
                    }}
                    secondaryAction={{
                        label: 'Plan a Visual System',
                        icon: FiMessageCircle,
                        conversionKey: 'visuals.hero.talk-to-expert',
                        channel: 'quote',
                        salesPath: 'managed',
                        onClick: openQuote
                    }}
                    stackActionsOnMobile
                    processLabel='How visual equipment rental works'
                    steps={heroSteps}
                />
            </PageShell>
      
       
   
    
            <PageShell>
                <VisualDecisionGuide
                    id='visualCategories'
                    eyebrow='Visual equipment decision guide'
                    heading='Choose the right display for your event'
                    description='The best format depends on the content, audience, viewing distance, ambient light, venue access, setup requirements, and budget. Start with the use case below, then compare the available systems.'
                    items={displayOptions}
                />
            </PageShell>

                <LandingCategories ui='v2' title options category='visuals' />
            <PastEvents ui='v2' newsletterevents={newsletterevents} total_events={total_events} />
            <ClientReviews ui='v2' />
        </div>
    </>
}

export default VisualsRental
